import { useState, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { z } from "zod";
import { Eye, EyeOff, Mail, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { lovable } from "@/integrations/lovable";

const emailSchema = z.string().trim().email("Invalid email address").max(255);
// Bangladesh-friendly: accept 10-15 digits, optional leading +
const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9]{10,15}$/, "Enter a valid phone number (10-15 digits)");
const passwordSchema = z.string().min(6, "Min 6 characters").max(72);
const nameSchema = z.string().trim().min(1, "Name is required").max(100);

type IdentifierKind = "email" | "phone" | "unknown";

const detectKind = (value: string): IdentifierKind => {
  const v = value.trim();
  if (!v) return "unknown";
  if (v.includes("@")) return "email";
  if (/^\+?[0-9\s-]+$/.test(v)) return "phone";
  return "unknown";
};

const normalizePhone = (value: string) => {
  const digits = value.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  // Default to Bangladesh country code if local format
  if (digits.startsWith("0") && digits.length >= 10) return "+88" + digits;
  return "+" + digits;
};

const getPasswordStrength = (pw: string) => {
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
};

const strengthMeta = [
  { label: "Too weak", color: "bg-destructive", width: "w-1/4" },
  { label: "Weak", color: "bg-destructive", width: "w-1/4" },
  { label: "Fair", color: "bg-yellow-500", width: "w-2/4" },
  { label: "Good", color: "bg-blue-500", width: "w-3/4" },
  { label: "Strong", color: "bg-green-500", width: "w-full" },
];

const Auth = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/auth",
      });
      if (result.error) {
        toast.error(result.error.message || "Could not sign in with Google");
        setGoogleLoading(false);
        return;
      }
      if (result.redirected) return;
      const { data: userData } = await supabase.auth.getUser();
      toast.success("Signed in with Google");
      const target = await resolveRedirect(userData.user?.id);
      setGoogleLoading(false);
      navigate(target);
    } catch (e: any) {
      toast.error(e?.message || "Google sign-in failed");
      setGoogleLoading(false);
    }
  };

  const [signinForm, setSigninForm] = useState({ identifier: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", identifier: "", password: "" });
  const [rememberMe, setRememberMe] = useState(true);

  const [showSigninPw, setShowSigninPw] = useState(false);
  const [showSignupPw, setShowSignupPw] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const signinKind = useMemo(() => detectKind(signinForm.identifier), [signinForm.identifier]);
  const signupKind = useMemo(() => detectKind(signupForm.identifier), [signupForm.identifier]);

  const pwStrength = getPasswordStrength(signupForm.password);

  const validateIdentifier = (value: string, kind: IdentifierKind) => {
    if (kind === "email") {
      emailSchema.parse(value);
      return { type: "email" as const, value: value.trim().toLowerCase() };
    }
    if (kind === "phone") {
      const normalized = normalizePhone(value);
      phoneSchema.parse(normalized);
      return { type: "phone" as const, value: normalized };
    }
    throw new Error("Enter a valid email or phone number");
  };

  const resolveRedirect = async (userId: string | undefined) => {
    if (redirect && redirect !== "/") return redirect;
    if (!userId) return "/";
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    return data ? "/admin" : "/";
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    let creds: { type: "email" | "phone"; value: string };
    try {
      creds = validateIdentifier(signinForm.identifier, signinKind);
      passwordSchema.parse(signinForm.password);
    } catch (err: any) {
      toast.error(err?.errors?.[0]?.message || err?.message || "Invalid input");
      return;
    }
    setLoading(true);
    const { data, error } =
      creds.type === "email"
        ? await supabase.auth.signInWithPassword({ email: creds.value, password: signinForm.password })
        : await supabase.auth.signInWithPassword({ phone: creds.value, password: signinForm.password });
    if (error) {
      setLoading(false);
      toast.error(error.message);
      return;
    }
    // "Remember me" off → clear session on browser close
    if (!rememberMe) {
      try {
        const raw = localStorage.getItem("sb-auth-token-temp-marker");
        // Move tokens to sessionStorage so they vanish when the tab closes
        const keys = Object.keys(localStorage).filter((k) => k.startsWith("sb-") && k.endsWith("-auth-token"));
        keys.forEach((k) => {
          const v = localStorage.getItem(k);
          if (v) {
            sessionStorage.setItem(k, v);
            localStorage.removeItem(k);
          }
        });
        if (raw) localStorage.removeItem("sb-auth-token-temp-marker");
      } catch {}
    }
    toast.success("Welcome back");
    const target = await resolveRedirect(data.user?.id);
    setLoading(false);
    navigate(target);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    let creds: { type: "email" | "phone"; value: string };
    try {
      nameSchema.parse(signupForm.name);
      creds = validateIdentifier(signupForm.identifier, signupKind);
      passwordSchema.parse(signupForm.password);
    } catch (err: any) {
      toast.error(err?.errors?.[0]?.message || err?.message || "Invalid input");
      return;
    }
    setLoading(true);

    // Create the user without OTP/email verification via the edge function (uses service role).
    const { data: created, error: createErr } = await supabase.functions.invoke("signup-no-otp", {
      body: {
        identifier: creds.value,
        password: signupForm.password,
        full_name: signupForm.name,
      },
    });

    if (createErr || (created && (created as any).error)) {
      setLoading(false);
      const msg =
        (created as any)?.error ||
        createErr?.message ||
        "Could not create your account";
      toast.error(msg);
      return;
    }

    // Auto-login immediately — no OTP, no email link.
    const { data: signInData, error: signInErr } =
      creds.type === "email"
        ? await supabase.auth.signInWithPassword({ email: creds.value, password: signupForm.password })
        : await supabase.auth.signInWithPassword({ phone: creds.value, password: signupForm.password });

    if (signInErr) {
      setLoading(false);
      toast.error(signInErr.message);
      return;
    }

    toast.success("Account created successfully");
    const target = await resolveRedirect(signInData.user?.id);
    setLoading(false);
    navigate(target);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      emailSchema.parse(forgotEmail);
    } catch (err: any) {
      toast.error(err?.errors?.[0]?.message || "Invalid email");
      return;
    }
    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setForgotLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Check your email for the reset link");
      setForgotOpen(false);
      setForgotEmail("");
    }
  };

  const IdentifierIcon = ({ kind }: { kind: IdentifierKind }) =>
    kind === "phone" ? (
      <Phone className="h-4 w-4 text-muted-foreground" />
    ) : (
      <Mail className="h-4 w-4 text-muted-foreground" />
    );

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-8">
          <h1 className="font-display text-3xl font-bold">RupShoili</h1>
          <p className="text-sm text-muted-foreground mt-1">Welcome back to honest shopping</p>
        </Link>
        <div className="rounded-3xl bg-card shadow-card p-6 sm:p-8">
          <Tabs defaultValue="signin">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="signin">Sign in</TabsTrigger>
              <TabsTrigger value="signup">Create account</TabsTrigger>
            </TabsList>

            {/* SIGN IN */}
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label htmlFor="si-id">Email or Phone Number</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2">
                      <IdentifierIcon kind={signinKind} />
                    </span>
                    <Input
                      id="si-id"
                      type="text"
                      inputMode={signinKind === "phone" ? "tel" : "email"}
                      autoComplete="username"
                      required
                      placeholder="you@example.com or 01XXXXXXXXX"
                      className="pl-10"
                      value={signinForm.identifier}
                      onChange={(e) => setSigninForm({ ...signinForm, identifier: e.target.value })}
                    />
                  </div>
                  {signinForm.identifier && signinKind === "unknown" && (
                    <p className="text-xs text-destructive mt-1">Enter a valid email or phone number</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="si-pass">Password</Label>
                    <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                        >
                          Forgot password?
                        </button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>Reset your password</DialogTitle>
                          <DialogDescription>
                            Enter your email and we'll send you a reset link.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleForgotPassword} className="space-y-4">
                          <div>
                            <Label htmlFor="fp-email">Email</Label>
                            <Input
                              id="fp-email"
                              type="email"
                              required
                              className="mt-1.5"
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                            />
                          </div>
                          <DialogFooter>
                            <Button type="submit" disabled={forgotLoading} className="w-full">
                              {forgotLoading ? "Sending..." : "Send reset link"}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="relative mt-1.5">
                    <Input
                      id="si-pass"
                      type={showSigninPw ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      className="pr-10"
                      value={signinForm.password}
                      onChange={(e) => setSigninForm({ ...signinForm, password: e.target.value })}
                    />
                    <button
                      type="button"
                      aria-label={showSigninPw ? "Hide password" : "Show password"}
                      onClick={() => setShowSigninPw((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showSigninPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={(c) => setRememberMe(c === true)}
                  />
                  <Label htmlFor="remember" className="text-sm font-normal cursor-pointer select-none">
                    Remember me
                  </Label>
                </div>

                <Button
                  type="submit"
                  variant="hero"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            {/* SIGN UP */}
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div>
                  <Label htmlFor="su-name">Full name</Label>
                  <Input
                    id="su-name"
                    required
                    className="mt-1.5"
                    value={signupForm.name}
                    onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="su-id">Email or Phone Number</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2">
                      <IdentifierIcon kind={signupKind} />
                    </span>
                    <Input
                      id="su-id"
                      type="text"
                      inputMode={signupKind === "phone" ? "tel" : "email"}
                      autoComplete="username"
                      required
                      placeholder="you@example.com or 01XXXXXXXXX"
                      className="pl-10"
                      value={signupForm.identifier}
                      onChange={(e) => setSignupForm({ ...signupForm, identifier: e.target.value })}
                    />
                  </div>
                  {signupForm.identifier && signupKind === "unknown" && (
                    <p className="text-xs text-destructive mt-1">Enter a valid email or phone number</p>
                  )}
                  {signupKind === "phone" && signupForm.identifier && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Will be saved as: {normalizePhone(signupForm.identifier)}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="su-pass">Password (min 6)</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="su-pass"
                      type={showSignupPw ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      className="pr-10"
                      value={signupForm.password}
                      onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                    />
                    <button
                      type="button"
                      aria-label={showSignupPw ? "Hide password" : "Show password"}
                      onClick={() => setShowSignupPw((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showSignupPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {signupForm.password && (
                    <div className="mt-2">
                      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                        <div
                          className={cn(
                            "h-full transition-all duration-300",
                            strengthMeta[pwStrength].color,
                            strengthMeta[pwStrength].width,
                          )}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Password strength:{" "}
                        <span className="font-medium text-foreground">
                          {strengthMeta[pwStrength].label}
                        </span>
                      </p>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="hero"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-3 text-muted-foreground">or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full gap-3 rounded-xl font-medium"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {googleLoading ? "Connecting..." : "Continue with Google"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Auth;
